import { getActiveGridTemplatesByUserProgramCategory } from '../../actions/gridTemplates/gridTemplatesActions';

// Azure-specific configuration
const isAzureEnvironment = () => {
  return window.location.hostname.includes('azurewebsites.net') || 
         window.location.hostname.includes('racetechnologies.com');
};

// Enhanced localStorage wrapper for Azure environments
const safeLocalStorage = {
  getItem: (key) => {
    try {
      const item = localStorage.getItem(key);
      if (item === null || item === undefined) {
        return null;
      }
      
      // Check for corrupted data patterns common in Azure
      if (item === '[object Object]' || item === 'undefined' || item === 'null') {
        console.warn(`Corrupted localStorage data detected for key: ${key}, clearing...`);
        localStorage.removeItem(key);
        return null;
      }
      
      return item;
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return null;
    }
  },
  
  setItem: (key, value) => {
    try {
      // Validate data before storing
      if (typeof value !== 'string') {
        console.error('Attempting to store non-string value in localStorage:', typeof value);
        return false;
      }
      
      // Test if it's valid JSON before storing
      if (value.startsWith('{') || value.startsWith('[')) {
        JSON.parse(value); // This will throw if invalid
      }
      
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error storing in localStorage:', error);
      // Clear the key if storage fails
      try {
        localStorage.removeItem(key);
      } catch (clearError) {
        console.error('Error clearing localStorage:', clearError);
      }
      return false;
    }
  },
  
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  }
};

export const loadCurrentTemplate = async (storageKey, user, programId, categoryId) => {
  try {
    console.log('loadCurrentTemplate from local', storageKey);
    
    // Try to load from localStorage first with enhanced error handling
    const localTemplate = safeLocalStorage.getItem(storageKey);
    if (localTemplate) {
      try {
        const parsedTemplate = JSON.parse(localTemplate);
        
        // Enhanced validation for Azure environment
        if (parsedTemplate && 
            typeof parsedTemplate === 'object' && 
            parsedTemplate.columns && 
            Array.isArray(parsedTemplate.columns) &&
            parsedTemplate.columns.length > 0) {
          
          console.log('Local template loaded with', parsedTemplate.columns.length, 'columns');
          return parsedTemplate;
        } else {
          console.warn('Invalid template structure in localStorage, clearing...');
          safeLocalStorage.removeItem(storageKey);
        }
      } catch (parseError) {
        console.error('Failed to parse template from localStorage:', parseError);
        safeLocalStorage.removeItem(storageKey);
      }
    } else {
      console.log('No local template found in localStorage');
    }

    // Fallback to server template with timeout for Azure
    console.log('Loading template from server...');
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Server template load timeout')), 10000)
    );
    
    const serverTemplatePromise = getActiveGridTemplatesByUserProgramCategory(user.id, programId, categoryId);
    
    try {
      const serverTemplates = await Promise.race([serverTemplatePromise, timeoutPromise]);
      
      if (serverTemplates) {
        // Handle both single object and array responses
        let templateValue;
        if (Array.isArray(serverTemplates) && serverTemplates.length > 0) {
          templateValue = serverTemplates[0].templateValue;
        } else if (serverTemplates.templateValue) {
          templateValue = serverTemplates.templateValue;
        }
        
        if (templateValue) {
          try {
            const serverTemplate = JSON.parse(templateValue);
            console.log('Server template loaded with', serverTemplate.columns?.length || 0, 'columns');
            return serverTemplate;
          } catch (parseError) {
            console.error('Failed to parse server template:', parseError);
          }
        }
      }
    } catch (timeoutError) {
      console.warn('Server template load timed out, continuing without template');
    }

    console.log('No template found - returning null for default state');
    return null;
  } catch (error) {
    console.error('Error loading template:', error);
    return null;
  }
};

export const validateAndCleanTemplate = (template, currentPermissions) => {
  if (!template || !template.columns) {
    return template;
  }

  // Azure-optimized template validation
  const validatedTemplate = {
    ...template,
    columns: template.columns.filter(col => {
      // More strict validation for Azure environment
      return col &&
             typeof col === 'object' &&
             col.dataField &&
             typeof col.dataField === 'string' &&
             col.dataField.length > 0;
    }).map(col => {
      // Create clean, serializable column objects
      const cleanColumn = {
        dataField: String(col.dataField),
        visible: Boolean(col.visible !== false), // Default to true unless explicitly false
      };
      
      // Only add optional properties if they exist and are valid
      if (typeof col.width === 'number' && col.width > 0) {
        cleanColumn.width = col.width;
      }
      if (typeof col.sortIndex === 'number') {
        cleanColumn.sortIndex = col.sortIndex;
      }
      if (col.sortOrder === 'asc' || col.sortOrder === 'desc') {
        cleanColumn.sortOrder = col.sortOrder;
      }
      if (col.filterValue !== undefined && col.filterValue !== null) {
        cleanColumn.filterValue = col.filterValue;
      }
      if (col.fixed === true) {
        cleanColumn.fixed = true;
      }
      if (col.fixedPosition === 'left' || col.fixedPosition === 'right') {
        cleanColumn.fixedPosition = col.fixedPosition;
      }
      
      return cleanColumn;
    })
  };

  console.log('Template after validation:', validatedTemplate.columns?.length || 0, 'columns');
  return validatedTemplate;
};

// Optimized saving with debouncing and Azure-specific handling
let saveTimeout = null;
const SAVE_DELAY = isAzureEnvironment() ? 2000 : 1000; // Longer delay in Azure

export const saveCurrentState = (gridInstance, storageKey) => {
  if (!gridInstance || !gridInstance.instance) {
    return;
  }

  // Clear any existing timeout to debounce rapid saves
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    try {
      const currentState = gridInstance.instance().state();
      if (!currentState || typeof currentState !== 'object') {
        console.warn('Invalid grid state, skipping save');
        return;
      }

      // Create Azure-optimized state object
      const optimizedState = {
        columns: (currentState.columns || []).map(col => ({
          dataField: String(col.dataField || ''),
          visible: Boolean(col.visible !== false),
          width: typeof col.width === 'number' && col.width > 0 ? col.width : undefined,
          sortIndex: typeof col.sortIndex === 'number' ? col.sortIndex : undefined,
          sortOrder: (col.sortOrder === 'asc' || col.sortOrder === 'desc') ? col.sortOrder : undefined,
          filterValue: (col.filterValue !== undefined && col.filterValue !== null) ? col.filterValue : undefined,
          fixed: col.fixed === true ? true : undefined,
          fixedPosition: (col.fixedPosition === 'left' || col.fixedPosition === 'right') ? col.fixedPosition : undefined
        })).filter(col => col.dataField.length > 0), // Remove invalid columns
        
        // Only include other state properties if they exist
        ...(currentState.searchText && { searchText: String(currentState.searchText) }),
        ...(currentState.expandedRowKeys && Array.isArray(currentState.expandedRowKeys) && { expandedRowKeys: currentState.expandedRowKeys }),
        ...(currentState.selectedRowKeys && Array.isArray(currentState.selectedRowKeys) && { selectedRowKeys: currentState.selectedRowKeys })
      };

      const stateString = JSON.stringify(optimizedState);
      
      // Validate JSON before storing
      JSON.parse(stateString); // This will throw if invalid
      
      const success = safeLocalStorage.setItem(storageKey, stateString);
      if (success) {
        console.log('saveCurrentState', storageKey, 'with', optimizedState.columns?.length || 0, 'columns');
      } else {
        console.error('Failed to save template state');
      }
    } catch (error) {
      console.error('Error saving template state:', error);
      // Clear potentially corrupted data
      safeLocalStorage.removeItem(storageKey);
    }
  }, SAVE_DELAY);
};

export const handleTemplateChange = async (gridInstance, storageKey, user, programId, categoryId, setTemplateError, setTemplateLoaded) => {
  try {
    // Add loading timeout for Azure environment
    const startTime = Date.now();
    const template = await loadCurrentTemplate(storageKey, user, programId, categoryId);
    const loadTime = Date.now() - startTime;
    
    console.log(`Template load took ${loadTime}ms`);
    
    if (template && gridInstance && gridInstance.instance) {
      const validatedTemplate = validateAndCleanTemplate(template, []);
      
      // Apply template with Azure-specific error handling
      try {
        // Use setTimeout to prevent blocking the UI thread
        setTimeout(() => {
          try {
            gridInstance.instance().state(validatedTemplate);
            setTemplateError(false);
            setTemplateLoaded(true);
            console.log('Template applied successfully');
          } catch (applyError) {
            console.error('Error applying template:', applyError);
            setTemplateError(true);
            safeLocalStorage.removeItem(storageKey);
          }
        }, 100);
      } catch (immediateError) {
        console.error('Immediate error in template application:', immediateError);
        setTemplateError(true);
        safeLocalStorage.removeItem(storageKey);
      }
    } else {
      console.log('No template available, allowing grid to continue');
      setTemplateLoaded(true);
    }
  } catch (error) {
    console.error('Error in handleTemplateChange:', error);
    setTemplateError(true);
    setTemplateLoaded(true);
  }
};

export const cleanFilterExpression = (filterExpression) => {
  return filterExpression;
};

// Azure environment diagnostics
export const diagnoseAzureEnvironment = () => {
  if (isAzureEnvironment()) {
    console.log('Azure Environment Detected:', {
      hostname: window.location.hostname,
      userAgent: navigator.userAgent,
      localStorage: {
        available: typeof(Storage) !== "undefined",
        quota: navigator.storage ? 'Available' : 'Not Available'
      }
    });
  }
};

// Call diagnostics on load
if (typeof window !== 'undefined') {
  diagnoseAzureEnvironment();
}